<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <ColumnToggle
          :table-name="'webhooksMaps'"
          :user-settings="userSettings"
          :settings-status="getUserSettingsStatus"
          :column-settings="columnSettings"
          @onChange="updateUserVisibleColumns"
        />
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('PLACEHOLDER_WEBHOOK_SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>
    <div
      v-if="!loading"
      class="m-b-15"
    >
      <!-- Create Button -->
      <button
        type="button"
        class="btn btn-success btn-sm"
        :disabled="showCreateForm"
        @click="displayCreateForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'ACTION_CREATE_WEBHOOK'" />
      </button>
    </div>

    <!-- Table Container -->
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="safeWebhooks"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              v-for="col in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :default-sort="columnSettings[col].defaultSort"
              :sort-key="columnSettings[col].name"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <!-- Create New Form -->
          <tr
            v-if="showCreateForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <WebhookForm
                :show-loader="createWebhookStatus.status === 'pending'
                  || updateWebhookStatus.status === 'pending'"
                :is-new-webhook="true"
                @cancel="hideCreateForm"
                @submit="onCreate"
              />
            </td>
          </tr>

          <!-- Rows -->
          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showCreateForm"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>

              <!-- Name -->
              <td
                v-if="userVisibleColumns.includes('name')"
                class="clickable"
                @click="displayEditForm(row.id)"
              >
                {{ row.name }}
              </td>

              <!-- Call Token -->
              <td v-if="userVisibleColumns.includes('callId')">
                {{ row.qPathMaps[0].incomingQueryStringName }}
              </td>

              <!-- Revenue Token -->
              <td v-if="userVisibleColumns.includes('revenue')">
                {{ row.qPathMaps[1].incomingQueryStringName }}
              </td>

              <!-- Fire Conversion -->
              <td v-if="userVisibleColumns.includes('parameterTest')">
                {{ row.parameterTest }}
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <WebhookActions
                  :current-webhook="row"
                  :disabled="disabled"
                  @edit="displayEditForm"
                  @confirm-delete="confirmDeleteWebhook"
                />
              </td>
            </tr>


            <!-- Webhook ID Row (Desktop) -->
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="`${row.id}-desktop`"
              :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
            >
              <td
                :colspan="row.mode === 'view' ? '7' : '6'"
                class="text-left p-t-0 p-b-10"
              >
                <b>Webhook:</b>&nbsp; {{ row.endPointUrl }}
              </td>
            </tr>



            <!-- Edit form -->
            <tr
              v-if="showEditForm(row.id)"
              :key="row.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <WebhookForm
                  :show-loader="showFormLoader"
                  :current-webhook="row"
                  :is-new-webhook="false"
                  @cancel="hideEditForm"
                  @submit="onEdit"
                />
              </td>
            </tr>


            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <WebhookActions
                  :current-webhook="row"
                  :disabled="disabled"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @edit="displayEditForm"
                  @confirm-delete="confirmDeleteWebhook"
                />
              </td>
            </tr>
          </template>

          <tr>
            <td
              v-if="emptyTable"
              v-t="'WEBHOOKS_TABLE_EMPTY'"
              colspan="100%"
            />
          </tr>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'WEBHOOKS_TABLE_LOADING'" />
    </div>
    <!-- Delete Confirmation Modal Start -->
    <ConfirmationModal
      v-if="showDeleteModal"
      :entity="webhookToDelete"
      :title="$t('TITLE_DELETE')"
      :cancel-button-text="$t('CANCEL')"
      :confirm-action-text="$t('DELETE')"
      :confirm-button-loading="deletingWebhook"
      @confirm="onDeleteWebhook"
      @close="closeDeleteModal"
    >
     <span v-html="$t('LABEL_DELETE_CONFIRMATION_MESSAGE', { name: webhookToDelete.name })">
        <br>
      </span>
    </ConfirmationModal>
    <!-- Delete Confirmation Modal End -->
  </PortalWidget>
</template>

<script>
import PortalWidget from '@/core/components/PortalWidget.vue';
import WebhookActions from './WebhookActions.vue';
import WebhookForm from './WebhookForm.vue';
import ConfirmationModal from '@/core/components/ConfirmationModal/ConfirmationModal';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle';
import messages from '../locales';
import Loader from '@/core/components/Loader';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import { webhookService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import * as actionTypes from '@/modules/webhooks/store/actionTypes';

export default {
  name: 'Webhooks',
  components: {
    Loader,
    WebhookForm,
    WebhookActions,
    PortalWidget,
    ColumnToggle,
    ConfirmationModal
  },
  data: () => ({
    currentPage: 1,
    totalPages: 0,
    showCreateForm: false,
    showDeleteModal: false,
    isMobile: isMobile,
    activeActionRowId: null,
    activeEditRowId: null,
    webhookToDelete: null,
    widget: null,
    filters: {
      name: { value: '', keys: ['name', 'id', 'mapToTagType','mapToTagName'] }
    }
  }),

  computed: {
    ...mapState('webHooks', [
      'webhooks',
      'userVisibleColumns'
    ]),

    ...mapGetters('webHooks', [
      'getWebhooksStatus',
      'createWebhookStatus',
      'updateWebhookStatus',
      'deleteWebhookStatus',
      'getUserVisibleColumnsStatus'
    ]),
    ...mapState('ui', [
      'userSettings',
    ]),
    ...mapGetters('ui', [
      'getUserSettingsStatus'
    ]),
    /** @type {() => any} */
    loading() {
      return this.getWebhooksStatus.status == STATUS_PENDING;
    },
    deletingWebhook() {
      return this.deleteWebhookStatus.status === STATUS_PENDING;
    },

    /** @type {() => boolean} */
    showFormLoader() {
      return this.createWebhookStatus.status === 'pending'
                    || this.updateWebhookStatus.status === 'pending';
    },

    /** @type {() => any} */
    emptyTable() {
      return this.getWebhooksStatus.status == STATUS_SUCCESS
      && this.safeWebhooks?.length == 0;
    },
    /** @type {() => any} */
    safeWebhooks() {
      if(this.getWebhooksStatus.status == STATUS_SUCCESS){
        return this.webhooks;
      }
      return [];
    },
    /** @type {() => any} */
    columnSettings() {
      return webhookService.columnSettings;
    },
    /** @type {() => any} */
    disabled() {
      return this.createWebhookStatus.status == STATUS_PENDING
      || this.updateWebhookStatus.status == STATUS_PENDING
      || this.showCreateForm
      || !!this.activeEditRowId;
    }
  },
  beforeMount() {
    this.widget = {
      title:this.$i18n.t('WEBHOOKS_HEADER'),
      iconClass: 'fa fa-cogs'
    };
  },
  watch: {
    currentPage() {
      this.hideEditForm();
      this.showCreateForm = false;
    },
    filters: {
      immediate: true,
      deep: true,
      handler() {
        this.hideEditForm();
      this.showCreateForm = false;
      }
    }
  },
  created() {
    this.getWebhooks();
    this.getUserSettings();
  },
  methods: {
    ...mapActions('webHooks', Object.values(actionTypes)),
    ...mapActions('users', [
      'getUserSettings'
    ]),
    /** @type {() => void} */
    updateUserVisibleColumns() {
      this.getUserVisibleColumns();
    },
    /** @type {() => void} */
    displayCreateForm() {
      this.hideEditForm();
      this.showCreateForm = true;
    },
    /** @type {() => void} */
    hideCreateForm() {
      this.showCreateForm = false;
    },
    /** @type {() => void} */
    displayEditForm(id) {
      this.hideCreateForm();
      this.activeEditRowId = id;
    },
    /** @type {() => void} */
    hideEditForm() {
      this.activeEditRowId = null;
    },
    /** @type {(rowId: string) => boolean} */
    showEditForm(rowId) {
      return !this.showCreateForm && !this.showEditRowId && this.activeEditRowId == rowId;
    },
    /** @type {() => void} */
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    /** @type {() => void} */
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    /** @type {() => void} */
    async onCreate(webhook) {
      await this.createWebhook(webhook);
      if (this.createWebhookStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_CREATE_SUCCESS'));
        this.hideCreateForm();
      }
      if (this.createWebhookStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createWebhookStatus.error);
      }
    },
    /** @type {() => void} */
    async onEdit(webhook) {
      await this.updateWebhook(webhook);
      if (this.updateWebhookStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_UPDATE_SUCCESS'));
        this.hideEditForm();
      }
      if (this.updateWebhookStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updateWebhookStatus.error);
      }
    },
    /** @type {() => void} */
    async onDeleteWebhook(webhook) {
      await this.deleteWebhook(webhook.id);
      if (this.deleteWebhookStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_DELETE_SUCCESS'));
      }
      if (this.deleteWebhookStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.deleteWebhookStatus.error);
      }
      this.showDeleteModal = false;
    },
    /** @type {() => void} */
    confirmDeleteWebhook(webhook) {
      this.webhookToDelete = {
        id: webhook.id,
        name: webhook.name
      };
      this.showDeleteModal = true;
    },
    /** @type {() => void} */
    closeDeleteModal() {
      this.showDeleteModal = false;
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
